<template>
  <v-container fluid>
    <v-dialog v-if="!disabled" v-model="showDialog" persistent max-width="500">
      <v-card>
        <v-card-title>
          {{ $t("module.masterTimePicker.selectTime") }}
        </v-card-title>
        <so-date-time-picker
          v-model="valueFromCalendar"
          continously-deconstruct
          not-menu
        />
        <v-card-actions>
          <v-btn text small color="primary" @click="forceUpdateFromDialog">{{
            $t("common.update")
          }}</v-btn>
          <v-btn text small color="gray" @click="showDialog = false">{{
            $t("common.close")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row>
      <v-col cols="12" :sm="split ? 6 : 12" :md="split ? 6 : 12">
        <h4>
          {{ $t("module.masterTimePicker.relativeTitle") }}
        </h4>
        <v-divider class="mb-4" />
        <v-row class="ml-1">
          <v-col v-for="(section, index) in relativeTimeRanges" :key="index">
            <v-row v-for="option in section.options" :key="option.index">
              <span
                @click="forceUpdateFromRelativeTime(option)"
                :disabled="disabled"
                style="width: 100%"
                :class="
                  disabled ? 'span-timerange-disabled' : 'span-timerange-option'
                "
              >
                {{
                  $t(
                    "module.masterTimePicker.relativeRanges." +
                      section.column +
                      "." +
                      option.index
                  )
                }}
              </span>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" :sm="split ? 6 : 12" :md="split ? 6 : 12">
        <h4>
          {{ $t("module.masterTimePicker.customTitle") }}
        </h4>
        <v-divider class="mb-4" />
        <v-row class="mx-1">
          <v-text-field
            v-model="textStart"
            :label="$t('module.masterTimePicker.selectStart')"
            append-outer-icon="mdi-calendar-clock"
            @click:append-outer="openDialog('start')"
            :rules="dateRules"
            :disabled="disabled"
            @blur="tryUpdate('start')"
          />
        </v-row>
        <v-row class="mx-1">
          <v-text-field
            v-model="textEnd"
            :label="$t('module.masterTimePicker.selectEnd')"
            append-outer-icon="mdi-calendar-clock"
            @click:append-outer="openDialog('end')"
            :rules="dateRules"
            :disabled="disabled"
            @blur="tryUpdate('end')"
          />
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import SoDateTimePicker from "./SoDateTimePicker.vue";
import { nowRegex, relativeRegex } from "@/_helpers/DateHelper.js";

const dateRegex =
  /^([0-9]{4})-([0-1][0-9]|[1][0-2])-([0-2][0-9]|[3][0-1])\s([0-1][0-9]|[2][0-3]):([0-5][0-9])$/;

export default {
  name: "TimeRangePicker",

  props: {
    value: {
      default: () => {
        return {
          start: undefined,
          end: undefined,
        };
      },
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    split: {
      type: Boolean,
      default: false,
    },
  },

  model: {
    prop: "value",
    event: "input",
  },

  emits: ["input", "change", "close"],

  computed: {
    localValue: {
      get() {
        return this.value;
      },

      set(val) {
        this.$emit("input", val);
      },
    },
  },

  components: {
    SoDateTimePicker,
  },

  data() {
    return {
      showDialog: false,
      valueFromCalendar: new Date(),
      selectingValue: undefined,
      textStart: this.value?.start,
      textEnd: this.value?.end,

      relativeTimeRanges: [
        {
          column: "minutes",
          options: [
            { index: 0, value: "-15m" },
            { index: 1, value: "-30m" },
            { index: 2, value: "-45m" },
          ],
        },
        {
          column: "hours",
          options: [
            { index: 0, value: "-1h" },
            { index: 1, value: "-6h" },
            { index: 2, value: "-12h" },
            { index: 3, value: "-24h" },
          ],
        },
        {
          column: "days",
          options: [
            { index: 0, value: "-2d" },
            { index: 1, value: "-7d" },
            { index: 2, value: "-14d" },
            { index: 3, value: "-30d" },
          ],
        },
      ],

      dateRules: [
        (v) =>
          !!v ||
          this.$t("validation.dateFormat", ["yyyy-MM-dd HH:mm or Relative"]),
        (v) =>
          dateRegex.test(v) ||
          relativeRegex.test(v) ||
          nowRegex.test(v) ||
          this.$t("validation.dateFormat", ["yyyy-MM-dd HH:mm or Relative"]),
      ],
    };
  },

  methods: {
    openDialog(selectedValue) {
      this.selectingValue = selectedValue;
      if (dateRegex.test(this.localValue[selectedValue])) {
        this.valueFromCalendar = new Date(this.localValue[selectedValue]);
      } else if (relativeRegex.test(this.localValue[selectedValue])) {
        this.valueFromCalendar = this.relativeTimeFluxSyntax(
          this.localValue[selectedValue].split(relativeRegex)
        );
      } else {
        this.valueFromCalendar = new Date();
      }

      this.showDialog = true;
    },

    forceUpdateFromDialog() {
      this.showDialog = false;
      this.localValue[this.selectingValue] = this.humanDate(
        this.valueFromCalendar,
        "yyyy-MM-dd HH:mm"
      );

      if (this.selectingValue === "start")
        this.textStart = this.localValue.start;

      if (this.selectingValue === "end") this.textEnd = this.localValue.end;

      this.$emit("change", this.selectingValue);
    },

    forceUpdateFromRelativeTime(value) {
      if (this.disabled) return;
      this.showDialog = false;
      this.localValue = {
        start: value.value,
        end: "Now",
      };

      this.textStart = value.value;
      this.textEnd = "Now";

      this.$emit("change");
      this.$emit("close");
    },

    tryUpdate(selectedValue) {
      let val = selectedValue === "start" ? this.textStart : this.textEnd;
      if (
        dateRegex.test(val) ||
        relativeRegex.test(val) ||
        nowRegex.test(val)
      ) {
        this.localValue = {
          start: this.textStart,
          end: this.textEnd,
        };
        this.$emit("change", selectedValue);
      }
    },
  },
};
</script>

<style scoped>
.span-timerange-option:hover {
  cursor: pointer;
  font-weight: bold;
}

.span-timerange-disabled {
  color: gray;
}
</style>
